import { generalId } from '@app/advisor-shared';
import { ModuleFE_Advisor_ColorGroup, ModuleFE_Advisor_DiseaseProfile, ModuleFE_Advisor_Expression, ModuleFE_Advisor_Pathway, ModuleFE_Advisor_Variable, ModuleFE_RuntimeFinding, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { SlackBuilderFE } from '@nu-art/slack-frontend/SlackBuilderFE';
import { Thunder } from '@nu-art/thunderstorm-frontend/index';
import { BadImplementationException, filterDuplicates, filterInstances, MissingDataException, ServerErrorSeverity } from '@nu-art/ts-common';
import { diagnosisIsOut } from './diagnosis-is-out.js';
import { RelatedFinding } from './RelatedFinding.js';
export class DiagnosisData {
    protocol;
    pathway;
    diagnosis;
    diseaseProfile;
    color;
    constructor(_protocol, diagnosisId) {
        const protocol = typeof _protocol === 'string' ? ModuleFE_RuntimeProtocol.cache.unique(_protocol) : _protocol;
        if (!protocol)
            throw new MissingDataException(`Could not get runtime protocol for id ${_protocol}`);
        if (protocol.variableIds[0] === generalId)
            throw new BadImplementationException('Trying to instantiate a DiagnosisData with general protocol');
        const pathway = ModuleFE_Advisor_Pathway.cache.unique(protocol.pathwayId);
        if (!pathway)
            throw new MissingDataException(`Could not get pathway for id ${protocol.pathwayId} for protocol with id ${protocol._id}`);
        const diagnosis = ModuleFE_Advisor_Variable.cache.unique(diagnosisId);
        if (!diagnosis)
            throw new MissingDataException(`Could not get diagnosis var for id ${diagnosisId}`);
        this.protocol = protocol;
        this.pathway = pathway;
        this.diagnosis = diagnosis;
        this.diseaseProfile = ModuleFE_Advisor_DiseaseProfile.cache.find(dp => dp.variableId === diagnosis._id);
        this.color = ModuleFE_Advisor_ColorGroup.getColorForDiseaseProfile(this.diseaseProfile);
    }
    //######################### Inner Logic #########################
    getRelatedRuntimeFindings = () => {
        const careMapOutcome = this.protocol.careMapOutcomes?.[this.diagnosis._id];
        if (!careMapOutcome)
            return [];
        const relevantFindingsIds = careMapOutcome.dpvsFindingsMap.flatMap(dpv => dpv.relatedFindings) ?? [];
        const relevantFindingsIdsCombined = relevantFindingsIds.reduce((finalMapper, currentValue) => {
            const indexOf = finalMapper.findIndex(item => item.findingId === currentValue.findingId);
            if (indexOf === -1) {
                finalMapper.push({
                    findingId: currentValue.findingId,
                    attrVariableIds: filterInstances([currentValue.attributeVariableId ?? undefined])
                });
                return finalMapper;
            }
            finalMapper[indexOf].attrVariableIds = filterInstances(filterDuplicates([...finalMapper[indexOf].attrVariableIds, currentValue.attributeVariableId]));
            return finalMapper;
        }, []);
        const missingIds = [];
        const relevantFindings = filterInstances(relevantFindingsIdsCombined.map(item => {
            let finding = ModuleFE_RuntimeFinding.cache.unique(item.findingId);
            if (!finding) // for now we cannot handle generated findings other than regular variable based findings
                missingIds.push(item.findingId);
            finding = { ...finding };
            if (finding.type === 'enumerated') // @ts-ignore
                finding.attributes = finding.attributes?.filter(attr => item.attrVariableIds.includes(attr.variableId));
            return finding;
        }));
        if (missingIds.length)
            new SlackBuilderFE(`_web-advisor-${Thunder.getInstance().getConfig().label.toLowerCase()}`)
                .addBlocks(SlackBuilderFE.TextSectionWithTitle(`${SlackBuilderFE.SeverityEmoji(ServerErrorSeverity.Warning)} Missing findings with the following Ids:`, `${missingIds.join('\n')}`)).send();
        return relevantFindings;
    };
    //######################### Public Logic #########################
    /**
     * Per-disease in/out for the cockpit lists. Does not mutate ``variableIds``.
     */
    isOut = () => {
        const finding = ModuleFE_RuntimeFinding.getLatestFindingForVarId(this.diagnosis._id);
        return diagnosisIsOut(finding, this.protocol.careMapOutcomes?.[this.diagnosis._id]?.ruleResponse?.ruleStatus);
    };
    getLabel = () => {
        return this.diagnosis.name;
    };
    getScoreReferences = () => {
        if (!this.diseaseProfile?.scoreReferences?.length)
            return [];
        return this.diseaseProfile.scoreReferences.reduce((refs, ref) => {
            const scoreVar = ModuleFE_Advisor_Variable.cache.unique(ref.variableId);
            const scoreExpr = ModuleFE_Advisor_Expression.cache.find(expr => expr.associatedVar === ref.variableId);
            if (scoreVar && scoreExpr)
                refs.push(ref);
            return refs;
        }, []);
    };
    getColor = () => {
        return this.color;
    };
    getRelatedFindings = (relatedFindings) => {
        relatedFindings ??= [];
        const newRelatedFindings = [];
        const relatedRuntimeFindings = this.getRelatedRuntimeFindings();
        if (!relatedRuntimeFindings.length)
            return relatedFindings;
        relatedRuntimeFindings.forEach(runtimeFinding => {
            const relatedFinding = RelatedFinding.createNew(runtimeFinding);
            if (!relatedFinding)
                return;
            relatedFinding.registerDiagnosis(this);
            const existing = relatedFindings.find(item => item.variable._id === relatedFinding.variable._id);
            if (!existing)
                newRelatedFindings.push(relatedFinding);
            else
                existing.merge(relatedFinding);
        });
        return [...relatedFindings, ...newRelatedFindings];
    };
}
//# sourceMappingURL=DiagnosisData.js.map